// HandoverDialog — the disable warning's "Hand over…" (ADR-081 §5): disabling an
// account edits no chain, so the operator hands the person's chains to successors
// BEFORE the lock, place by place, through the same body the leave dialog uses and
// the same choke point (the backing consent is the shared confirm-on-save flow).
// Only the places with a pick are sent — an untouched place is left exactly as it
// is (a "none" here is no decision, unlike the leave's gaps); Save waits for one.
// Picks are work: every close path asks to discard them first. Saving changes the
// chains alone; the disable stays the operator's next act.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DepartureChain } from '@ems-app/schemas/users/responses';
import { Alert } from '../../components/ui/Alert.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { BackingConfirmDialog } from '../../components/patterns/BackingConfirmDialog.js';
import { useStagedGuard } from '../../components/patterns/useStagedGuard.js';
import { useHandover } from '../../hooks/useUserMutations.js';
import { useResponsibilityChainSave } from '../../hooks/useResponsibilityChainSave.js';
import { HandoverBody, chainKey, useBackingConsent, type SuccessorPick } from './HandoverBody.js';

export interface HandoverDialogProps {
  projectId: string;
  user: { user_id: string; name: string };
  chains: readonly DepartureChain[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function HandoverDialog({ projectId, user, chains, open, onClose, onSaved }: HandoverDialogProps) {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const handover = useHandover(projectId);
  const [picks, setPicks] = useState<Record<string, SuccessorPick | null>>({});
  const busy = handover.isPending;
  // The places WITH a pick — the rest are not sent, so they stay as they are.
  const places = chains.flatMap((c) => {
    const p = picks[chainKey(c)];
    return p ? [{ responsibility_id: c.responsibility_id, level: c.level, scope_id: c.scope_id, successor_id: p.id }] : [];
  });
  const chainSave = useResponsibilityChainSave({
    submit: (grantBacking) => handover.mutateAsync({ userId: user.user_id, body: { places, grant_backing: grantBacking } }),
    onSaved: () => {
      setPicks({});
      onSaved();
    },
    tErrors,
  });
  const consent = useBackingConsent(projectId, chains, picks, chainSave.backingPrompt);
  const close = () => {
    setPicks({});
    chainSave.reset();
    onClose();
  };
  // Every close path (Escape · backdrop · ✕ · Cancel) routes through the guard: a pick asks first, none closes straight through.
  const guard = useStagedGuard({
    open,
    dirty: places.length > 0,
    busy,
    onClose: close,
    discardTitle: t('departure.discardTitle'),
    discardBody: t('departure.discardBody'),
  });
  return (
    <>
      <Modal
        open={open}
        onClose={guard.requestClose}
        title={t('departure.dialogTitle', { name: user.name })}
        maxWidth="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={guard.requestClose} disabled={busy}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="primary" onClick={chainSave.save} disabled={busy || places.length === 0} loading={busy}>
              {t('departure.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('departure.help')}</p>
          <HandoverBody projectId={projectId} userId={user.user_id} chains={chains} successors={picks} onChange={(k, pick) => setPicks((s) => ({ ...s, [k]: pick }))} disabled={busy} />
          {chainSave.error && <Alert variant="danger">{chainSave.error}</Alert>}
        </div>
      </Modal>
      {guard.discardDialog}
      <BackingConfirmDialog open={chainSave.backingPrompt !== null} place={consent.place} names={consent.names} onConfirm={chainSave.confirmBacking} onClose={chainSave.dismissBacking} />
    </>
  );
}
